// client/src/api/monitoring.js
import flex from './FlexFetch'
import { unwrapStrictData } from './contractUtils'

/**
 * Monitoring API Service
 * Frontend interface for monitoring and alerting endpoints
 */

const monitoring_endpoint = '/api/1.0/monitoring/'

function normalizeMonitoringResponse(payload) {
  const data = unwrapStrictData(payload)

  if (data === payload) {
    return payload
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (Object.prototype.hasOwnProperty.call(data, 'success')) {
      return data
    }

    return {
      success: true,
      ...data,
    }
  }

  return data
}

async function requestMonitoring(path, options) {
  const response = await flex.flexFetch(monitoring_endpoint + path, options)
  return normalizeMonitoringResponse(await flex.handleResponse(response))
}

/**
 * Get current system health status
 * @returns {Promise} Health status with incidents
 */
export async function getHealthStatus() {
  return requestMonitoring('health-status', {
    credentials: 'include',
  })
}

/**
 * Get incidents with optional filtering
 * @param {Object} filters - Filter options
 * @param {number} filters.limit - Maximum number of incidents
 * @param {string} filters.severity - Filter by severity (info/warning/critical)
 * @param {boolean} filters.unresolvedOnly - Only show unresolved incidents
 * @param {string} filters.startDate - Start date for range filter
 * @param {string} filters.endDate - End date for range filter
 * @returns {Promise} List of incidents
 */
export async function getIncidents(filters = {}) {
  const params = new URLSearchParams()
  if (filters.limit) params.append('limit', filters.limit)
  if (filters.severity) params.append('severity', filters.severity)
  if (filters.unresolvedOnly) params.append('unresolved_only', 'true')
  if (filters.startDate) params.append('start_date', filters.startDate)
  if (filters.endDate) params.append('end_date', filters.endDate)
  return requestMonitoring(`incidents?${params}`, {
    credentials: 'include',
  })
}

/**
 * Get metric aggregates
 * @param {Object} filters - Filter options
 * @param {string} filters.metricName - Name of metric to fetch
 * @param {string} filters.startDate - Start date for range filter
 * @param {string} filters.endDate - End date for range filter
 * @param {number} filters.limit - Maximum number of records
 * @returns {Promise} Metric aggregates
 */
export async function getMetrics(filters = {}) {
  const params = new URLSearchParams()
  if (filters.metricName) params.append('metric_name', filters.metricName)
  if (filters.startDate) params.append('start_date', filters.startDate)
  if (filters.endDate) params.append('end_date', filters.endDate)
  if (filters.limit) params.append('limit', filters.limit)
  return requestMonitoring(`metrics?${params}`, {
    credentials: 'include',
  })
}

/**
 * Trigger metric collection manually
 * @param {number} intervalMinutes - Time interval for collection
 * @returns {Promise} Collection result
 */
export async function collectMetrics(intervalMinutes = 5) {
  return requestMonitoring(
    `collect-metrics?interval_minutes=${intervalMinutes}`,
    {
      method: 'POST',
      credentials: 'include',
    },
  )
}

/**
 * Trigger threshold evaluation manually
 * @returns {Promise} Evaluation result
 */
export async function evaluateThresholds() {
  return requestMonitoring('evaluate-thresholds', {
    method: 'POST',
    credentials: 'include',
  })
}

/**
 * Generate health digest (preview only)
 * @param {string} startDate - Start date (optional)
 * @param {string} endDate - End date (optional)
 * @returns {Promise} Digest data
 */
export async function generateDigest(startDate = null, endDate = null) {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)

  return requestMonitoring(`generate-digest?${params}`, {
    credentials: 'include',
  })
}

/**
 * Generate and post daily digest to GitHub
 * @param {string} startDate - Start date (optional)
 * @param {string} endDate - End date (optional)
 * @returns {Promise} Post result
 */
export async function postDailyDigest(startDate = null, endDate = null) {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)

  return requestMonitoring(`post-daily-digest?${params}`, {
    method: 'POST',
    credentials: 'include',
  })
}

/**
 * Reload threshold configuration
 * @returns {Promise} Reload result
 */
export async function reloadThresholds() {
  return requestMonitoring('reload-thresholds', {
    method: 'POST',
    credentials: 'include',
  })
}
